import { useEffect, useState } from "react";
import { Menu, X, Mail, Phone, Instagram } from "lucide-react";
import Logo from "./Logo";
import TopBar from "./TopBar";

const links = [
  { label: "Home", href: "#top" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Contact", href: "#contact" },
];

const socials = [
  {
    icon: Instagram,
    href: "https://www.instagram.com/vish.capital",
    label: "Instagram",
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      id="top"
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 bg-white transition-all duration-500 ${
        scrolled
          ? "shadow-[0_1px_0_rgba(0,0,0,0.08)]"
          : "border-b border-[#EFEFEA]"
      }`}
    >
      <div
        className={`overflow-hidden transition-all duration-300 ${
          scrolled ? "max-h-0 opacity-0" : "max-h-10 opacity-100"
        }`}
      >
        <TopBar />
      </div>
      <div className="mx-auto max-w-7xl px-6 md:px-10 h-[96px] flex items-center justify-between gap-8">
        <Logo />

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => {
                if (l.href === "#top") {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-[13.5px] tracking-wide text-[#1E293B] hover:text-[#0F172A] relative transition-colors group"
            >
              {l.label}
              <span className="absolute left-0 -bottom-1 h-px w-0 bg-[#C9A227] transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            data-testid="navbar-cta-book-consultation"
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white px-5 py-2.5 text-[13px] font-medium tracking-wide hover:bg-[#1E293B] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_-12px_rgba(15,23,42,0.5)]"
          >
            Book Consultation
            <span className="h-1.5 w-1.5 rounded-full bg-[#C9A227]" />
          </a>

          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="navbar-hamburger"
            aria-label="Open menu"
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#EFEFEA] text-[#0F172A] hover:border-[#0F172A]/30 transition-colors"
          >
            <Menu size={20} strokeWidth={1.75} />
          </button>
        </div>
      </div>

      {/* Mobile overlay */}
      <div
        onClick={() => setOpen(false)}
        data-testid="navbar-mobile-overlay"
        className={`fixed inset-0 z-[60] bg-[#0F172A]/40 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      />

      {/* Mobile slide-in sidebar */}
      <aside
        data-testid="navbar-mobile-sidebar"
        className={`fixed top-0 right-0 z-[70] h-full w-[82%] max-w-[340px] bg-white shadow-[-20px_0_60px_-20px_rgba(15,23,42,0.35)] transition-transform duration-400 ease-[cubic-bezier(0.22,1,0.36,1)] lg:hidden flex flex-col ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-6 h-[80px] border-b border-[#EFEFEA] shrink-0">
          <Logo />
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid="navbar-close-menu"
            aria-label="Close menu"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#EFEFEA] text-[#0F172A] hover:border-[#0F172A]/30 transition-colors"
          >
            <X size={20} strokeWidth={1.75} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <nav className="flex flex-col px-6 py-8 gap-1">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => {
                  setOpen(false);
                  if (l.href === "#top") {
                    e.preventDefault();
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }}
                data-testid={`nav-link-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-[16px] tracking-wide text-[#1E293B] hover:text-[#C9A227] py-3.5 border-b border-[#F5F5F0] transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="px-6">
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              data-testid="navbar-mobile-cta-book-consultation"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0F172A] text-white px-5 py-3 text-[14px] font-medium tracking-wide hover:bg-[#1E293B] transition-all duration-300"
            >
              Book Consultation
              <span className="h-1.5 w-1.5 rounded-full bg-[#C9A227]" />
            </a>
          </div>

          {/* Contact details */}
          <div className="px-6 mt-8 pt-6 border-t border-[#F1F1EC] space-y-4">
            <a
              href="mailto:customercare@vishcapital.com"
              data-testid="navbar-mobile-email"
              className="flex items-center gap-3 text-[13.5px] text-[#334155] hover:text-[#0F172A] transition-colors"
            >
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#FAFAF8] border border-[#EFEFEA] text-[#C9A227] shrink-0">
                <Mail size={15} strokeWidth={1.75} />
              </span>
              customercare@vishcapital.com
            </a>
            <a
              href="tel:+919345093450"
              data-testid="navbar-mobile-phone"
              className="flex items-center gap-3 text-[13.5px] text-[#334155] hover:text-[#0F172A] transition-colors"
            >
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#FAFAF8] border border-[#EFEFEA] text-[#C9A227] shrink-0">
                <Phone size={15} strokeWidth={1.75} />
              </span>
              +91 93450 93450
            </a>
          </div>
        </div>

        {/* Social media icons */}
        <div className="px-6 py-6 border-t border-[#F1F1EC] shrink-0">
          <div className="flex items-center gap-3">
            {socials.map(({ icon: Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`navbar-mobile-social-${label.toLowerCase()}`}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#FAFAF8] border border-[#EFEFEA] text-[#0F172A] hover:bg-[#0F172A] hover:text-white hover:border-[#0F172A] transition-all duration-300"
              >
                <Icon size={15} strokeWidth={1.75} />
              </a>
            ))}
          </div>
        </div>
      </aside>
    </header>
  );
}